import type {
  FilterRuleCreateInput,
  FilterRuleUpdateInput,
  MailboxFilterRule,
} from "@cdt/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { FILTER_RULES_QUERY_KEY } from "@/hooks/useFilterRulesQuery";
import { ApiError } from "@/lib/api";
import {
  createFilterRule,
  deleteFilterRule,
  updateFilterRule,
} from "@/lib/filtersApi";

function handleError(error: unknown, fallback: string) {
  if (error instanceof ApiError) {
    const body = error.body as { error?: string } | null;
    if (body?.error) {
      toast.error(body.error);
      return;
    }
  }
  const message = error instanceof Error ? error.message : String(error);
  toast.error(`${fallback}: ${message}`);
}

export function useCreateFilterRule() {
  const qc = useQueryClient();
  return useMutation<MailboxFilterRule, unknown, FilterRuleCreateInput>({
    mutationFn: (payload) => createFilterRule(payload),
    onSuccess: () => {
      toast.success("Regola creata");
      qc.invalidateQueries({ queryKey: FILTER_RULES_QUERY_KEY });
    },
    onError: (error) => handleError(error, "Creazione fallita"),
  });
}

export function useUpdateFilterRule() {
  const qc = useQueryClient();
  return useMutation<
    MailboxFilterRule,
    unknown,
    { id: string; patch: FilterRuleUpdateInput }
  >({
    mutationFn: ({ id, patch }) => updateFilterRule(id, patch),
    onSuccess: () => {
      toast.success("Regola aggiornata");
      qc.invalidateQueries({ queryKey: FILTER_RULES_QUERY_KEY });
    },
    onError: (error) => handleError(error, "Aggiornamento fallito"),
  });
}

export function useDeleteFilterRule() {
  const qc = useQueryClient();
  return useMutation<void, unknown, { id: string }>({
    mutationFn: ({ id }) => deleteFilterRule(id),
    onSuccess: () => {
      toast.success("Regola eliminata");
      qc.invalidateQueries({ queryKey: FILTER_RULES_QUERY_KEY });
    },
    onError: (error) => handleError(error, "Eliminazione fallita"),
  });
}
